/**
 * TinyMCE UI-language packs: what is available, and which one a setting
 * resolves to.
 *
 * Scope: these packs translate the editor CHROME — menus, dialogs, tooltips.
 * They do not touch message content, the spellchecker (English dictionary) or
 * Harper (English only). Choosing German gets you a German toolbar around an
 * unchanged English writing experience.
 *
 * TinyMCE has NO auto-detection: its `language` option defaults to "en", and
 * for anything else it fetches `<baseURL>/langs/<code>.js` and raises an
 * in-editor error notification if that file is missing. Detection is therefore
 * ours, and we must only ever hand it a code we shipped a pack for.
 *
 * The packs are copied next to tinymce.min.js by bin/build-tinymce.js, which
 * also writes langs/index.json listing what it copied. Resolving against that
 * manifest — rather than a hardcoded list — means the copied files stay the
 * single source of truth for what can be offered, and a custom
 * `mailx-tinymce-cdn` override is followed automatically because the manifest
 * URL is derived from the script URL by the same rule TinyMCE uses.
 *
 * (Claude Code 2026-08-23)
 */

/** Setting value meaning "follow the browser's language list". */
export const LANGUAGE_AUTO = "auto";
/** TinyMCE's built-in strings. No pack, no fetch. */
export const LANGUAGE_ENGLISH = "en";

/** Where build-tinymce.js wrote the list of packs it copied, derived from the
 *  TinyMCE script URL exactly as TinyMCE derives `langs/<code>.js`. */
export function langManifestUrl(tinymceScriptUrl: string): string {
    return new URL("langs/index.json", new URL(tinymceScriptUrl, location.href)).href;
}

/** Language codes we have packs for, e.g. ["ar", "de", "pt-BR", …]. Empty when
 *  the manifest is missing (an older build, or a CDN override pointing at a
 *  tree without our langs/ directory) — callers then stay on English rather
 *  than provoking TinyMCE's load-error notification. */
export async function availableLanguages(tinymceScriptUrl: string): Promise<string[]> {
    try {
        const r = await fetch(langManifestUrl(tinymceScriptUrl));
        if (!r.ok) return [];
        const list = await r.json();
        return Array.isArray(list) ? list.filter(x => typeof x === "string") : [];
    } catch {
        return [];
    }
}

/**
 * Resolve a stored setting to a code TinyMCE can actually load.
 *
 * `setting` is either LANGUAGE_AUTO, LANGUAGE_ENGLISH, or a pack code. A code
 * we no longer ship falls back to English rather than failing at load time.
 *
 * Auto walks the browser's ordered preference list and takes the first hit:
 * an exact pack ("pt-BR"), then the bare language ("pt"), then any regional
 * pack for that language ("pt" → "pt-BR"). English short-circuits — it needs
 * no pack. Matching is case-insensitive because BCP-47 region subtags are
 * conventionally upper-case in the pack names and browsers are not required
 * to agree.
 */
export function resolveLanguage(
    setting: string,
    available: readonly string[],
    preferred: readonly string[] = navigator.languages || [navigator.language],
): string {
    const byLower = new Map(available.map(code => [code.toLowerCase(), code]));

    if (setting && setting !== LANGUAGE_AUTO) {
        if (setting === LANGUAGE_ENGLISH) return LANGUAGE_ENGLISH;
        return byLower.get(setting.toLowerCase()) || LANGUAGE_ENGLISH;
    }

    for (const tag of preferred) {
        if (!tag) continue;
        const lower = tag.toLowerCase();
        const base = lower.split("-")[0];
        if (base === LANGUAGE_ENGLISH) return LANGUAGE_ENGLISH;
        const hit = byLower.get(lower)
            || byLower.get(base)
            || available.find(code => code.toLowerCase().split("-")[0] === base);
        if (hit) return hit;
    }
    return LANGUAGE_ENGLISH;
}

/** Human-readable name for a pack code, in the reader's own language —
 *  `Intl.DisplayNames` already knows every one of these, so no name table is
 *  maintained here. Falls back to the raw code on a runtime that doesn't. */
export function languageLabel(code: string): string {
    try {
        const names = new Intl.DisplayNames(navigator.languages || undefined, { type: "language" });
        return names.of(code) || code;
    } catch {
        return code;
    }
}
